import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@library/components/navigation';
import { EnterpriseFeatureConfigService } from 'app/services/enterprise-feature-config.service';
import { EnterpriseService } from 'app/services/enterprise.service';
import { SecurityProfileService } from 'app/services/securityProfile.service';
import { UserService } from 'app/services/user.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ROLES } from 'shared-models';
import { canUserTrackTimeInEnterprise } from '../auth/timerecord-role.util';
import { compactNavigation } from './navigation.data';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _navigation: BehaviorSubject<{ compact: FuseNavigationItem[] }> = new BehaviorSubject<{
    compact: FuseNavigationItem[];
  }>({
    compact: [],
  });

  private securityProfileService = inject(SecurityProfileService);
  private enterpriseFeatureConfigService = inject(EnterpriseFeatureConfigService);
  private userService = inject(UserService);
  private enterpriseService = inject(EnterpriseService);
  private _cachedRole: ROLES | null = null;
  private _cachedFeatureSignature = '';
  private _cachedNavigation: FuseNavigationItem[] = [];

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get navigation$(): Observable<{ compact: FuseNavigationItem[] }> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get navigation
   */
  get(): Observable<{ compact: FuseNavigationItem[] }> {
    const userRole = this.securityProfileService.getRole();
    const featureConfig = this.enterpriseFeatureConfigService.getSnapshot();
    const canTrackTime = canUserTrackTimeInEnterprise(
      this.userService.getUser(),
      this.enterpriseService.selectedEnterprise?.id
    );
    const featureSignature = [
      featureConfig.fileManagementEnabled,
      featureConfig.applicationsEnabled,
      featureConfig.projectsEnabled,
      // Sin esto, activar o desactivar el planner no refrescaría el menú: la navegación
      // está cacheada y solo se recalcula cuando cambia esta firma.
      featureConfig.plannerEnabled,
      // ⚠️ Y por lo mismo estos dos. Olvidar un flag aquí no rompe nada visible: el filtro de
      // abajo es correcto, simplemente no se vuelve a ejecutar, así que el menú se queda con
      // el de la empresa anterior. Sin error y sin log. Lo caza `navigation.service.spec.ts`.
      featureConfig.timeRecordsEnabled,
      featureConfig.reportsEnabled,
      this.enterpriseService.selectedEnterprise?.id || '',
      canTrackTime,
    ].join('|');

    if (this._cachedRole !== userRole || this._cachedFeatureSignature !== featureSignature) {
      this._cachedRole = userRole;
      this._cachedFeatureSignature = featureSignature;

      const filteredByFeature = compactNavigation.filter((item) => {
        if (item.id === 'file-manager-user' && !featureConfig.fileManagementEnabled) {
          return false;
        }

        if (item.id === 'applications' && !featureConfig.applicationsEnabled) {
          return false;
        }

        if (item.id === 'my-assignments' && !featureConfig.projectsEnabled) {
          return false;
        }

        if ((item.id === 'planner' || item.id === 'my-shifts') && !featureConfig.plannerEnabled) {
          return false;
        }

        /**
         * Fichaje y Analíticas están construidas enteras sobre horas fichadas, así que se
         * van juntas. `home-dashboard` también: el dashboard de empresa son KPIs de jornada.
         */
        /**
         * ⚠️ **`=== false` y no `!`, a propósito.** Es el mismo fallo abierto que en el
         * servicio: solo se esconde cuando alguien lo ha apagado de verdad. Con `!`, un
         * `undefined` —una config a medio construir, un mock incompleto— escondería el
         * producto principal, que es exactamente el error que esta tarea intenta evitar.
         */
        const fichajeApagado = featureConfig.timeRecordsEnabled === false;

        if (
          (item.id === 'time-tracking' || item.id === 'analytics' || item.id === 'home-dashboard') &&
          fichajeApagado
        ) {
          return false;
        }

        // Informes sobrevive si queda algún tipo que enseñar: el de proyectos no depende del
        // fichaje. Por eso mira la derivada y no el módulo.
        if (item.id === 'reports' && featureConfig.reportsEnabled === false) {
          return false;
        }

        // `admin-shifts` apunta a `/time-tracking/automatizations`, que queda tras el guard:
        // dejarlo visible sería un enlace que rebota.
        if (item.id === 'admin-shifts' && fichajeApagado) {
          return false;
        }

        return true;
      });

      const filteredByTrackableAdmin =
        userRole === ROLES.ADMIN && canTrackTime
          ? filteredByFeature.filter((item) => item.id !== 'home-dashboard')
          : filteredByFeature;

      if (userRole === ROLES.SUPERADMIN) {
        this._cachedNavigation = filteredByTrackableAdmin.filter((item) => item.id !== 'home-dashboard');
      } else if (userRole === ROLES.CONFIG_ADMIN) {
        this._cachedNavigation = filteredByTrackableAdmin.filter(
          (item) => item.id === 'configuration-panel' || (item.roles && item.roles.includes(ROLES.CONFIG_ADMIN))
        );
      } else {
        this._cachedNavigation = filteredByTrackableAdmin.filter((item) => {
          if (!item.roles) return true;

          if (Array.isArray(item.roles) && userRole !== null && item.roles.includes(userRole)) {
            return true;
          }

          if (userRole === ROLES.ADMIN && canTrackTime && Array.isArray(item.roles)) {
            return item.roles.includes(ROLES.USER) || item.roles.includes(ROLES.MANAGER);
          }

          return false;
        });
      }

      this._navigation.next({
        compact: this._cachedNavigation,
      });
    }

    return this._navigation.asObservable();
  }

  /**
   * Update the navigation
   *
   * @param navigation
   */
  update(navigation: FuseNavigationItem[]): void {
    // Update the navigation
    this._navigation.next({
      compact: navigation,
    });
  }
}
